import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A modern URL state management hook inspired by the Medium article approach.
 * Provides a clean way to manage state in URL search parameters with type safety.
 *
 * @param key - The URL parameter key
 * @param defaultValue - Default value if the parameter doesn't exist
 * @param options - Configuration options
 * @returns Object with value, setValue, and clearValue functions
 *
 * @example
 * ```typescript
 * // For string values
 * const { value: period, setValue: setPeriod } = useUrlState('period', 'past30Days');
 *
 * // For object values (automatically serialized/deserialized)
 * const { value: filters, setValue: setFilters } = useUrlState('filters', {});
 *
 * // For array values
 * const { value: selectedIds, setValue: setSelectedIds } = useUrlState('ids', []);
 * ```
 */
export function useUrlState<T>(
  key: string,
  defaultValue: T,
  options: {
    /** Whether to serialize/deserialize the value (default: true for objects/arrays) */
    serialize?: boolean;
    /** Custom serializer function */
    serializer?: (value: T) => string;
    /** Custom deserializer function */
    deserializer?: (value: string) => T;
  } = {}
): {
  value: T;
  setValue: (value: T | ((prev: T) => T)) => void;
  clearValue: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine if we should serialize based on value type and options
  const shouldSerialize = useMemo(() => {
    if (options.serialize !== undefined) return options.serialize;
    return typeof defaultValue === 'object' && defaultValue !== null;
  }, [defaultValue, options.serialize]);

  // Default serializer/deserializer functions
  const serialize = useCallback(
    (value: T): string => {
      if (options.serializer) return options.serializer(value);
      return JSON.stringify(value);
    },
    [options.serializer]
  );

  const deserialize = useCallback(
    (value: string): T => {
      if (options.deserializer) return options.deserializer(value);
      try {
        return JSON.parse(value);
      } catch {
        return defaultValue;
      }
    },
    [options.deserializer, defaultValue]
  );

  // Get current value from URL
  const value = useMemo(() => {
    const paramValue = searchParams.get(key);
    if (paramValue === null) return defaultValue;

    if (shouldSerialize) {
      return deserialize(paramValue);
    }

    return paramValue as T;
  }, [key, searchParams, defaultValue, shouldSerialize, deserialize]);

  // Set value in URL
  const setValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      setSearchParams((prev) => {
        const prevParams = new URLSearchParams(prev);
        const resolvedValue = typeof newValue === 'function' ? (newValue as (prev: T) => T)(value) : newValue;

        if (resolvedValue === null || resolvedValue === undefined) {
          prevParams.delete(key);
        } else {
          const stringValue = shouldSerialize ? serialize(resolvedValue) : String(resolvedValue);
          prevParams.set(key, stringValue);
        }

        return prevParams;
      });
    },
    [key, setSearchParams, shouldSerialize, serialize, value]
  );

  // Clear value from URL
  const clearValue = useCallback(() => {
    setSearchParams((prev) => {
      const prevParams = new URLSearchParams(prev);
      prevParams.delete(key);
      return prevParams;
    });
  }, [key, setSearchParams]);

  return {
    value,
    setValue,
    clearValue,
  };
}

/**
 * Hook for managing multiple URL state values at once.
 * Useful when you have related state that should be managed together.
 *
 * @param stateConfig - Object defining the state keys and their default values
 * @returns Object with values and setters for each state key
 *
 * @example
 * ```typescript
 * const { values, setters } = useUrlStates({
 *   period: { defaultValue: 'past30Days' },
 *   tab: { defaultValue: 'policy' },
 *   filters: { defaultValue: {}, serialize: true }
 * });
 *
 * // Access values
 * console.log(values.period, values.tab, values.filters);
 *
 * // Update values
 * setters.period('past3Months');
 * setters.tab('training');
 * ```
 */
export function useUrlStates<T extends Record<string, any>>(stateConfig: {
  [K in keyof T]: {
    defaultValue: T[K];
    serialize?: boolean;
    serializer?: (value: T[K]) => string;
    deserializer?: (value: string) => T[K];
  };
}): {
  values: T;
  setters: {
    [K in keyof T]: (value: T[K] | ((prev: T[K]) => T[K])) => void;
  };
  clearAll: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get all values
  const values = useMemo(() => {
    const result = {} as T;

    for (const [key, config] of Object.entries(stateConfig)) {
      const paramValue = searchParams.get(key);

      if (paramValue === null) {
        result[key as keyof T] = config.defaultValue;
      } else {
        const shouldSerialize =
          config.serialize ?? (typeof config.defaultValue === 'object' && config.defaultValue !== null);

        if (shouldSerialize) {
          try {
            result[key as keyof T] = config.deserializer ? config.deserializer(paramValue) : JSON.parse(paramValue);
          } catch {
            result[key as keyof T] = config.defaultValue;
          }
        } else {
          result[key as keyof T] = paramValue as T[keyof T];
        }
      }
    }

    return result;
  }, [searchParams, stateConfig]);

  // Create setters
  const setters = useMemo(() => {
    const result = {} as {
      [K in keyof T]: (value: T[K] | ((prev: T[K]) => T[K])) => void;
    };

    for (const [key, config] of Object.entries(stateConfig)) {
      result[key as keyof T] = (newValue: T[keyof T] | ((prev: T[keyof T]) => T[keyof T])) => {
        setSearchParams((prev) => {
          const prevParams = new URLSearchParams(prev);
          const resolvedValue =
            typeof newValue === 'function'
              ? (newValue as (prev: T[keyof T]) => T[keyof T])(values[key as keyof T])
              : newValue;

          if (resolvedValue === null || resolvedValue === undefined) {
            prevParams.delete(key);
          } else {
            const shouldSerialize =
              config.serialize ?? (typeof config.defaultValue === 'object' && config.defaultValue !== null);
            const stringValue = shouldSerialize
              ? config.serializer
                ? config.serializer(resolvedValue)
                : JSON.stringify(resolvedValue)
              : String(resolvedValue);
            prevParams.set(key, stringValue);
          }

          return prevParams;
        });
      };
    }

    return result;
  }, [setSearchParams, stateConfig, values]);

  // Clear all values
  const clearAll = useCallback(() => {
    setSearchParams((prev) => {
      const prevParams = new URLSearchParams(prev);
      Object.keys(stateConfig).forEach((key) => {
        prevParams.delete(key);
      });
      return prevParams;
    });
  }, [setSearchParams, stateConfig]);

  return {
    values,
    setters,
    clearAll,
  };
}

export default useUrlState;
